import React from "react";
import styled from "styled-components";
import { Container } from "./Container";
import StarIcon from "@mui/icons-material/Star";
import { ReactComponent as CuescriptLogo } from "../assets/images/Cuescript-logo.svg";

const Section = styled.section`
  padding: var(--section-y) 0;
  background: var(--bg-1);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Header = styled.div`
  display: grid;
  gap: 14px;
  margin-bottom: 26px;
  text-align: center;
`;

const Title = styled.h2`
  color: var(--text-0);
  font-size: clamp(28px, 3.6vw, 44px);
  letter-spacing: -0.03em;
  line-height: 1.08;
`;

const StarRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const Star = styled(StarIcon)`
  color: var(--accent-0);
  filter: drop-shadow(0 10px 26px rgba(185, 255, 102, 0.14));
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 18px;
  margin-top: 22px;
`;

const Card = styled.div`
  border: 1px solid var(--border-0);
  background: var(--surface-0);
  border-radius: var(--radius-lg);
  padding: 20px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.18) inset;

  display: grid;
  grid-template-rows: 1fr auto;
  gap: 18px;

  transition: transform 180ms ease, background 180ms ease,
    border-color 180ms ease;

  &:hover {
    transform: translateY(-2px);
    background: var(--surface-1);
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

const Quote = styled.p`
  font-size: 15px;
  font-style: italic;
  color: rgba(255, 255, 255, 0.76);
`;

const Byline = styled.div`
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 10px;
`;

const LogoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  min-width: 148px;
  padding: 0 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const BylineText = styled.p`
  color: rgba(255, 255, 255, 0.86);
  font-weight: 850;
  font-size: 14px;
`;

const LogoImg = styled.img`
  height: 28px;
  width: auto;
  transform: scale(${({ $zoom }) => $zoom || 1});
  transform-origin: center;
`;

const LogoSvg = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  padding-top: ${({ $padTop }) => ($padTop ? `${$padTop}px` : "0px")};

  svg {
    display: block;
    width: 112px;
    height: auto;
  }
`;

export const Testimonials = () => {
  return (
    <Section id="testimonials">
      <Container>
        <Header>
          <Title>Testimonials</Title>
          <StarRow aria-label="Five star rating">
            <Star fontSize="large" />
            <Star fontSize="large" />
            <Star fontSize="large" />
            <Star fontSize="large" />
            <Star fontSize="large" />
          </StarRow>
        </Header>

        <Grid>
          <Card>
            <Quote>
              "We worked with Nate on our website design for our business
              rebrand and the results were outstanding. We were able to
              collaborate and share our thoughts during the process and he was
              able to interpret our ideas into an effective and visually
              appealing website. This website has brought us continuous
              inquiries and revenue to our business. We highly recommend working
              with Nate on your next website or development project."
            </Quote>
            <Byline>
              <LogoBadge>
                <LogoImg
                  src={require("../assets/images/lebrunshvaclogo.png")}
                  alt="Lebrun's HVAC Services logo"
                  $zoom={1.18}
                />
              </LogoBadge>
              <BylineText>Lebrun's HVAC Services</BylineText>
            </Byline>
          </Card>

          <Card>
            <Quote>
              "Nate is a dedicated and passionate programmer that has made many
              contributions to CueScript's success in the teleprompting
              industry. Never shying away from innovative technology, he laid
              the groundwork for CueScript's speech following prompter
              technology, "Say-iT". He wrote clean and efficient code that
              helped the next generation of programmers to continue where he
              left off. He continues to be a trusted resource for CueScript's
              development team."
            </Quote>
            <Byline>
              <LogoBadge aria-label="CueScript logo">
                <LogoSvg $padTop={4}>
                  <CuescriptLogo />
                </LogoSvg>
              </LogoBadge>
              <BylineText>CueScript</BylineText>
            </Byline>
          </Card>

          <Card>
            <Quote>
              "Nate has been a pleasure to work with in his role as a full-stack
              engineer. He jumped into a challenging environment - a small
              startup that needed to move fast and leverage new technologies he
              wasn’t familiar with. He easily rose to the challenge. It was
              established early on that he would not require much supervision as
              he quickly and consistently completed his work, while utilizing
              his previous experience to identify and implement new ways to
              enhance our workflow. His efforts got our new site up and running
              right on schedule. He was proactive, positive, and professional
              and we would work with him again on future projects without
              hesitation."
            </Quote>
            <Byline>
              <LogoBadge>
                <LogoImg
                  src={require("../assets/images/noteworthyai.webp")}
                  alt="NoteworthyAI logo"
                  style={{ height: 24 }}
                />
              </LogoBadge>
              <BylineText>NoteworthyAI</BylineText>
            </Byline>
          </Card>

          <Card>
            <Quote>
              "Working with Nate was a pleasure! The website that he created for
              our business is easy to update and manage! He is always responsive
              to any questions I have! Looking forward to working with him for
              many more projects! Highly recommended!"
            </Quote>
            <Byline>
              <LogoBadge>
                <LogoImg
                  src={require("../assets/images/bonzanmi_logo.PNG")}
                  alt="Bonzanmi Haitian Cuisine logo"
                  $zoom={1.22}
                />
              </LogoBadge>
              <BylineText>Bonzanmi Haitian Cuisine</BylineText>
            </Byline>
          </Card>
        </Grid>
      </Container>
    </Section>
  );
};
